import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Stack, DownloadSimple, Crown, ArrowRight } from "@phosphor-icons/react";

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bundles").then((r) => {
      setBundles(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-4">
          <Stack weight="fill" size={22} className="text-[#FFEB3B]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFEB3B]">Theme Bundles</span>
        </div>
        <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter uppercase">
          The whole look, one tap
        </h1>
        <p className="text-white/60 text-lg mt-5 max-w-2xl">
          Matching icon pack, widget, and wallpaper — bundled into a single set. Download it all at once for a
          seamless home screen.
        </p>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={`bundle-skeleton-${i}`} className="h-72 rounded-2xl glass animate-pulse" />
            ))}
          </div>
        ) : bundles.length === 0 ? (
          <p className="text-white/40 py-20 text-center">No bundles yet. Check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mt-12" data-testid="bundles-grid">
            {bundles.map((b) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Link
                  to={`/bundle/${b.id}`}
                  data-testid={`bundle-card-${b.id}`}
                  className="group relative block rounded-2xl overflow-hidden glass hover:border-[#FFEB3B]/50 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img src={b.cover_image} alt={b.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent" />
                    <span className="absolute top-4 left-4 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#05050A] bg-[#FFEB3B] px-2.5 py-1 rounded-sm">
                      <Stack weight="fill" size={12} /> {b.item_ids.length}-piece set
                    </span>
                    {b.featured && (
                      <span className="absolute top-4 right-4 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#FFEB3B] bg-[#05050A]/70 px-2 py-1 rounded-sm">
                        <Crown weight="fill" size={12} /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-black text-2xl tracking-tighter uppercase group-hover:text-[#FFEB3B] transition-colors">
                      {b.title}
                    </h3>
                    <p className="text-white/60 mt-3 leading-relaxed line-clamp-2">{b.description}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="flex items-center gap-1 text-white/40 text-sm">
                        <DownloadSimple size={15} /> {b.download_count}
                      </span>
                      <span className="flex items-center gap-2 text-[#FFEB3B] font-bold text-sm group-hover:gap-3 transition-all">
                        View bundle <ArrowRight weight="bold" size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
